<script setup lang="ts">
import { onMounted, ref } from 'vue';
import DxForm, { DxSimpleItem, DxLabel, DxGroupItem } from 'devextreme-vue/form';
import { DxButton } from 'devextreme-vue/button';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { getAggregatorsLookup, BottoPagePanel, LookupBox, showError } from '@botto/core';
import { fetchReport, REPORT_GROUP_BY_OPTIONS, type ReportQuery } from '../../api';
import {
  getManagersLookup,
  getPartnersLookup,
  getSmppClientsLookup,
  reloadAccountsLookups,
} from '../../api/adapters/accounts';
import { getDirectionsLookup } from '../../lookups';
import DynamicResultGrid from '../../components/DynamicResultGrid.vue';

const directionsLookup = getDirectionsLookup();
const aggregatorsLookup = getAggregatorsLookup();
const clientsLookup = getSmppClientsLookup();
const managersLookup = getManagersLookup();
const partnersLookup = getPartnersLookup();

const loading = ref(false);
const result = ref<unknown>(null);

const filters = ref<ReportQuery & { group_by: string[] }>({
  group_by: ['created_day'],
  report_currency: 'RUB',
  date_from: null,
  date_to: null,
  client_id: null,
  manager_id: null,
  partner_id: null,
  direction_id: null,
  country_iso: null,
  operator: null,
  aggregator_id: null,
});

const groupByItems = REPORT_GROUP_BY_OPTIONS.map((o) => o.value);

function formatDateForApi(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 00:00:00`;
}

async function runReport() {
  loading.value = true;
  result.value = null;
  try {
    const q: ReportQuery = {
      group_by: (filters.value.group_by?.length ? filters.value.group_by : undefined) as ReportQuery['group_by'],
      report_currency: filters.value.report_currency || undefined,
      client_id: filters.value.client_id ?? undefined,
      manager_id: filters.value.manager_id ?? undefined,
      partner_id: filters.value.partner_id ?? undefined,
      direction_id: filters.value.direction_id ?? undefined,
      country_iso: filters.value.country_iso || undefined,
      operator: filters.value.operator || undefined,
      aggregator_id: filters.value.aggregator_id ?? undefined,
      date_from: formatDateForApi(filters.value.date_from as Date | string | null),
      date_to: formatDateForApi(filters.value.date_to as Date | string | null),
    };
    result.value = await fetchReport(q);
  } catch (e) {
    showError(e);
  } finally {
    loading.value = false;
  }
}

function setFilter<K extends keyof typeof filters.value>(field: K, value: (typeof filters.value)[K]) {
  filters.value[field] = value;
}

onMounted(() => {
  void directionsLookup.ensureLoaded();
  void aggregatorsLookup.ensureLoaded();
  void reloadAccountsLookups();
});
</script>

<template>
  <BottoPagePanel title="Отчёт" standalone>
    <template #actions>
      <DxButton
        text="Сформировать"
        type="default"
        styling-mode="contained"
        :disabled="loading"
        @click="runReport"
      />
    </template>

    <div class="reports-page">
      <DxForm :form-data="filters" :col-count="2" label-location="top">
      <DxGroupItem :col-span="2" caption="Период и группировка">
        <DxSimpleItem
          data-field="date_from"
          editor-type="dxDateBox"
          :editor-options="{ type: 'date', displayFormat: 'dd.MM.yyyy' }"
        >
          <DxLabel text="Дата с" />
        </DxSimpleItem>
        <DxSimpleItem
          data-field="date_to"
          editor-type="dxDateBox"
          :editor-options="{ type: 'date', displayFormat: 'dd.MM.yyyy' }"
        >
          <DxLabel text="Дата по" />
        </DxSimpleItem>
        <DxSimpleItem
          data-field="group_by"
          editor-type="dxTagBox"
          :col-span="2"
          :editor-options="{ items: groupByItems, showSelectionControls: true, searchEnabled: true }"
        >
          <DxLabel text="Группировка (group_by)" />
        </DxSimpleItem>
        <DxSimpleItem data-field="report_currency">
          <DxLabel text="Валюта отчёта" />
        </DxSimpleItem>
      </DxGroupItem>

      <DxGroupItem :col-span="2" caption="Фильтры">
        <DxSimpleItem>
          <DxLabel text="Канал" />
          <LookupBox
            :model-value="filters.client_id"
            :lookup="clientsLookup"
            placeholder="SMPP-канал"
            @update:model-value="(v) => setFilter('client_id', v as number | null)"
          />
        </DxSimpleItem>
        <DxSimpleItem>
          <DxLabel text="Менеджер" />
          <LookupBox
            :model-value="filters.manager_id"
            :lookup="managersLookup"
            placeholder="Менеджер"
            @update:model-value="(v) => setFilter('manager_id', v as number | null)"
          />
        </DxSimpleItem>
        <DxSimpleItem>
          <DxLabel text="Партнёр" />
          <LookupBox
            :model-value="filters.partner_id"
            :lookup="partnersLookup"
            placeholder="Партнёр"
            @update:model-value="(v) => setFilter('partner_id', v as number | null)"
          />
        </DxSimpleItem>
        <DxSimpleItem>
          <DxLabel text="Направление" />
          <LookupBox
            :model-value="filters.direction_id"
            :lookup="directionsLookup"
            placeholder="Направление"
            @update:model-value="(v) => setFilter('direction_id', v as number | null)"
          />
        </DxSimpleItem>
        <DxSimpleItem>
          <DxLabel text="Агрегатор" />
          <LookupBox
            :model-value="filters.aggregator_id"
            :lookup="aggregatorsLookup"
            placeholder="Агрегатор"
            @update:model-value="(v) => setFilter('aggregator_id', v as number | null)"
          />
        </DxSimpleItem>
        <DxSimpleItem data-field="country_iso">
          <DxLabel text="Country ISO" />
        </DxSimpleItem>
        <DxSimpleItem data-field="operator">
          <DxLabel text="Оператор" />
        </DxSimpleItem>
      </DxGroupItem>
    </DxForm>

      <div v-if="result != null" class="reports-page__result">
        <DynamicResultGrid :data="result" :loading="loading" />
      </div>
    </div>
  </BottoPagePanel>

  <DxLoadPanel :visible="loading" :show-indicator="true" :shading="true" />
</template>

<style scoped>
.reports-page {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 4px;
}

.reports-page__result {
  flex: 0 0 auto;
  min-height: 280px;
}
</style>
